import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function TermsPage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Terms of Service | Brushour Painters & Decorators Watford',
    description: 'Read the Brushour terms of service. Understand the conditions for using our professional painting and decorating services in Watford, Hertfordshire.',
    keywords: 'Brushour terms of service, painting company terms Watford',
    canonical: `${SITE_URL}/terms`,
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Terms of Service – Brushour',
        description: 'Terms of service for Brushour painting and decorating services in Watford, Hertfordshire.',
        url: `${SITE_URL}/terms`,
        publisher: {
          '@type': 'Organization',
          name: 'Brushour',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
          },
        },
      },
    ],
  });

  useEffect(() => {
    // Ensure the scroll is performed only in a browser environment
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-neutral-800 mb-8">
            Terms of Service
          </h1>
          <p className="text-sm text-neutral-500 mb-12">
            Last updated: January 2025
          </p>

          <div className="space-y-8 text-neutral-700">
            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                1. Agreement to Terms
              </h2>
              <p className="leading-relaxed">
                By accessing our website or using our painting and decorating
                services, you agree to be bound by these Terms of Service. If
                you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                2. Our Services
              </h2>
              <p className="leading-relaxed mb-4">
                Brushour provides professional painting and decorating services
                in Watford, Hertfordshire and surrounding areas, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Interior and exterior painting</li>
                <li>Wallpaper hanging and removal</li>
                <li>Surface preparation and repairs</li>
                <li>Colour consultation</li>
                <li>Commercial and residential projects</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                3. Quotes and Pricing
              </h2>
              <p className="leading-relaxed">
                All quotes provided are estimates based on the information
                available at the time of assessment. Final pricing may vary if
                additional work is required or if the scope of work changes. We
                will always discuss any changes with you before proceeding.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                4. Booking and Cancellation
              </h2>
              <p className="leading-relaxed mb-4">When you book our services:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>A deposit may be required to secure your booking</li>
                <li>Cancellations must be made at least 48 hours in advance</li>
                <li>Late cancellations may incur a fee</li>
                <li>
                  We reserve the right to reschedule due to weather conditions
                  for exterior work
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                5. Payment Terms
              </h2>
              <p className="leading-relaxed">
                Payment is due upon completion of work unless otherwise agreed
                in writing. We accept bank transfer, cash, and major credit/debit
                cards. For larger projects, staged payments may be arranged.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                6. Warranty and Guarantee
              </h2>
              <p className="leading-relaxed">
                We take pride in our workmanship and offer a satisfaction
                guarantee on all our services. If you are not completely
                satisfied with our work, please contact us within 14 days of
                completion and we will address any concerns.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                7. Client Responsibilities
              </h2>
              <p className="leading-relaxed mb-4">
                To ensure smooth service delivery, clients are expected to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide clear access to work areas</li>
                <li>Remove or protect valuable items from work areas</li>
                <li>Inform us of any known hazards or issues</li>
                <li>Ensure adequate ventilation during and after work</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                8. Limitation of Liability
              </h2>
              <p className="leading-relaxed">
                While we take every precaution to protect your property,
                Brushour shall not be liable for any indirect, incidental, or
                consequential damages. Our liability is limited to the value of
                the services provided.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                9. Intellectual Property
              </h2>
              <p className="leading-relaxed">
                All content on this website, including text, images, logos, and
                design, is the property of Brushour and is protected by copyright
                law. You may not reproduce, distribute, or use any content without
                our written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                10. Changes to Terms
              </h2>
              <p className="leading-relaxed">
                We reserve the right to modify these terms at any time. Changes
                will be effective immediately upon posting to our website. Your
                continued use of our services constitutes acceptance of any
                changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                11. Governing Law
              </h2>
              <p className="leading-relaxed">
                These terms are governed by the laws of England and Wales. Any
                disputes shall be subject to the exclusive jurisdiction of the
                courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">
                12. Contact Information
              </h2>
              <p className="leading-relaxed">
                For any questions regarding these Terms of Service, please
                contact us:
              </p>
              <div className="mt-4 p-4 bg-beige-50 rounded-lg">
                <p className="font-medium">Brushour</p>
                <p>Watford, Hertfordshire</p>
                <p>Email: daniel@brushour.co.uk</p>
                <p>Phone: 07595037441</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
